import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { recordEvent, ensureSession } from "@/lib/analytics";

// Dynamic import of the supabase client to keep the heavy supabase-js
// bundle (auth + postgrest + storage + realtime, ~150KB minified) OUT of
// the LCP-critical main entry chunk. Auth state is hydrated asynchronously
// after first paint — guests are unaffected because sign-in is optional
// site-wide. Authenticated views (/account/*) gate on `loading` already.
let _supabasePromise: Promise<typeof import("@/integrations/supabase/client")["supabase"]> | null = null;
function getSupabase() {
  if (!_supabasePromise) {
    _supabasePromise = import("@/integrations/supabase/client").then((m) => m.supabase);
  }
  return _supabasePromise;
}

/**
 * Customer-facing auth context. Separate concept from admin auth (which lives
 * in AdminLayout's role check). The same auth.users session powers both, but
 * only this context is consumed by the public site / /account routes.
 *
 * Sign-in is OPTIONAL everywhere on the public site. Guests can complete
 * quotes without signing in. When they later sign up using the same email,
 * a Postgres trigger links their guest quote_requests to their new user_id.
 */

type Ctx = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signUpWithPassword: (
    email: string,
    password: string,
    metadata?: {
      full_name?: string;
      phone?: string;
      company?: string;
      signup_source?: string;
      signup_source_page?: string;
      marketing_consent?: boolean;
    },
  ) => Promise<{ error: string | null; needsEmailConfirm: boolean }>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const CustomerAuthContext = createContext<Ctx | null>(null);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    void getSupabase().then((supabase) => {
      if (cancelled) return;
      // Set up listener BEFORE the initial getSession (per Supabase guidance)
      const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      setUser(next?.user ?? null);
      setLoading(false);
      // Best-effort analytics for auth lifecycle.
      try {
        if (event === "SIGNED_IN") {
          void ensureSession();
          void recordEvent("login", { metadata: { provider: next?.user?.app_metadata?.provider ?? null } });
          // Brand-new accounts (email/password OR OAuth like Google) get a
          // one-time welcome email. We detect "new" by comparing the user's
          // created_at with last_sign_in_at — for a freshly-created user
          // these are within seconds of each other on the very first sign-in.
          // The idempotency key is per-user so even if this fires more than
          // once, only one email is ever delivered.
          try {
            const u = next?.user;
            const createdAt = u?.created_at ? Date.parse(u.created_at) : NaN;
            const lastSignIn = u?.last_sign_in_at ? Date.parse(u.last_sign_in_at) : NaN;
            // First-ever sign-in: created_at and last_sign_in_at are within
            // ~seconds of each other for brand-new accounts (covers both
            // email/password and OAuth like Google). The server-side
            // `notify-buyer-event` endpoint uses an idempotency key keyed
            // on user.id so even if this fires more than once only one
            // welcome email is ever delivered.
            const isFreshAccount =
              Number.isFinite(createdAt) &&
              Number.isFinite(lastSignIn) &&
              Math.abs(lastSignIn - createdAt) < 60_000;
            if (u && isFreshAccount && (u.email ?? "").trim()) {
              void supabase.functions
                .invoke("notify-buyer-event", {
                  body: { event: "account-welcome" },
                })
                .then(({ error: sendErr }) => {
                  if (sendErr) {
                    console.warn("[auth] welcome email send failed", sendErr);
                  }
                });
            }
          } catch (welcomeErr) {
            console.warn("[auth] welcome email block threw", welcomeErr);
          }
          // Best-effort: link any guest quote_requests this user submitted
          // (matched by email, server-side). Idempotent — safe to call on
          // every sign-in. Covers the case where an EXISTING user submits a
          // guest quote and then signs in (the signup trigger only fires for
          // brand-new accounts). Never blocks the sign-in flow.
          void supabase.rpc("link_my_guest_requests").then(({ data, error }) => {
            if (error) {
              console.warn("[auth] link_my_guest_requests failed", error);
              return;
            }
            if (typeof data === "number" && data > 0) {
              void recordEvent("guest_requests_linked", { metadata: { count: data } });
            }
          });
        } else if (event === "SIGNED_OUT") {
          void recordEvent("logout");
        }
      } catch {
        /* never break auth on analytics */
      }
      });
      unsub = () => sub.subscription.unsubscribe();
      supabase.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      });
    });
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    return { error: error?.message ?? null };
  }, []);

  const signUpWithPassword = useCallback(
    async (email: string, password: string, metadata?: { full_name?: string; phone?: string }) => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/account`,
          data: metadata ?? {},
        },
      });
      if (error) return { error: error.message, needsEmailConfirm: false };
      // If email confirmation is required, session will be null until verified.
      const needsEmailConfirm = !data.session;
      // Fire-and-forget account_created event.
      try {
        void recordEvent("account_created", {
          metadata: { needs_email_confirm: needsEmailConfirm, has_full_name: Boolean(metadata?.full_name) },
        });
      } catch {
        /* ignore */
      }
      return { error: null, needsEmailConfirm };
    },
    [],
  );

  const resetPassword = useCallback(async (email: string) => {
    const supabase = await getSupabase();
    await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/account/reset-password`,
    });
  }, []);

  const signOut = useCallback(async () => {
    const supabase = await getSupabase();
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<Ctx>(
    () => ({ user, session, loading, signInWithPassword, signUpWithPassword, resetPassword, signOut }),
    [user, session, loading, signInWithPassword, signUpWithPassword, resetPassword, signOut],
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  return ctx;
}